/**
 * FiscalizeButton — odnotowanie fiskalizacji dokumentu sprzedaży.
 *
 * CO TO ROBI: zapisuje przy dokumencie numer paragonu wydrukowanego na drukarce fiskalnej.
 * CZEGO NIE ROBI: nie drukuje. Przeglądarka nie ma dostępu do drukarki fiskalnej — steruje nią
 * Symfonia na stanowisku sprzedaży. Nazwanie przycisku samym „Fiskalizuj" bez tego wyjaśnienia
 * sugerowałoby, że klik wypluje paragon; dlatego okno mówi wprost, czego oczekuje.
 *
 * Przycisk pojawia się TYLKO dla dokumentu, który da się zafiskalizować:
 *   • w buforze — Symfonia odmawia, więc przycisku nie ma (zamiast dawać klik pod odmowę),
 *   • już zafiskalizowany — zamiast przycisku widać stan, bo to informacja, nie akcja.
 * Operacji nie da się cofnąć przez WebAPI, stąd potwierdzenie przed wysłaniem.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Receipt } from 'lucide-react';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

export function FiscalizeButton({
  documentId,
  companyId,
  inBuffer,
  fiscalized,
  onDone,
}: {
  documentId: number | string;
  companyId: string | null;
  inBuffer: boolean;
  fiscalized: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [deviceNo, setDeviceNo] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        receiptNumber: Number(receiptNumber),
        ...(deviceNo.trim() ? { fiscalDeviceNo: deviceNo.trim() } : {}),
      };
      return (
        await api.post(
          `/handel-magazyn/sales/${documentId}/fiscalize?companyId=${companyId}`,
          payload,
        )
      ).data;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.handelMagazyn.fiscal.done') });
      setOpen(false);
      setReceiptNumber('');
      setDeviceNo('');
      onDone();
    },
    onError: (err) => {
      // Odmowa Symfonii („w buforze", „już zafiskalizowany") to ODPOWIEDŹ, nie awaria — niesie
      // powód, więc pokazujemy go zamiast ogólnego „nie udało się".
      const data = isAxiosError(err)
        ? (err.response?.data as { message?: string; details?: { body?: string } } | undefined)
        : undefined;
      toast.show({
        variant: 'error',
        title: t('tenant.handelMagazyn.fiscal.failed'),
        description: data?.message ?? data?.details?.body ?? undefined,
      });
    },
  });

  if (fiscalized) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800"
        data-testid="hm-fiscal-status"
      >
        <Receipt className="h-3.5 w-3.5" aria-hidden />
        {t('tenant.handelMagazyn.fiscal.already')}
      </span>
    );
  }

  // Dokument w buforze nie podlega fiskalizacji — Symfonia odmówi, więc nie dajemy klika pod odmowę.
  if (inBuffer) return null;

  const canSubmit = Number.isInteger(Number(receiptNumber)) && Number(receiptNumber) > 0;

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<Receipt className="h-4 w-4" />}
        onClick={() => setOpen(true)}
        data-testid="hm-fiscal-open"
      >
        {t('tenant.handelMagazyn.fiscal.action')}
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('tenant.handelMagazyn.fiscal.title')}
        description={t('tenant.handelMagazyn.fiscal.subtitle')}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={!canSubmit}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {t('tenant.handelMagazyn.fiscal.action')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t('tenant.handelMagazyn.fiscal.warning')}
          </p>
          <Input
            label={t('tenant.handelMagazyn.fiscal.receiptNumber')}
            value={receiptNumber}
            inputMode="numeric"
            onChange={(e) => setReceiptNumber(e.target.value)}
            required
            data-testid="hm-fiscal-receipt"
          />
          <Input
            label={t('tenant.handelMagazyn.fiscal.deviceNo')}
            value={deviceNo}
            onChange={(e) => setDeviceNo(e.target.value)}
            hint={t('tenant.handelMagazyn.fiscal.deviceHint')}
            data-testid="hm-fiscal-device"
          />
        </div>
      </Modal>
    </>
  );
}
